import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';


interface NavbarProps {}

const Navbar: React.FC<NavbarProps> = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<{ [key: string]: boolean }>({});
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.navbar-container')) {
        setOpenDropdowns({});
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = (key: string) => {
    setOpenDropdowns(prev => {
      // Close all other dropdowns and toggle the current one
      const newState: { [key: string]: boolean } = {};
      newState[key] = !prev[key];
      return newState;
    });
  };

  const closeAllDropdowns = () => {
    setOpenDropdowns({});
    setIsOpen(false);
  };

  const getDropdownPosition = (index: number, totalItems: number) => {
    // For the last few items, align dropdown to the right
    if (index >= totalItems - 2) {
      return 'dropdown-right';
    }
    // For middle items, center the dropdown
    if (index >= 2 && index < totalItems - 2) {
      return 'dropdown-center';
    }
    // Default left alignment for first items
    return '';
  };

  const menuItems = [
    {
      label: 'Compliance',
      children: [
        { path: '/compliance/pci-dss', label: 'PCI DSS' },
        { path: '/compliance/iso-27001', label: 'ISO 27001' },
        { path: '/compliance/cbj-compliance', label: 'CBJ Compliance' },
        { path: '/compliance/web-risk-monitoring', label: 'Web Risk Monitoring' }
      ]
    },
    {
      label: 'Services',
      children: [
        { path: '/services/risk-assessment', label: 'Risk Assessment' },
        { path: '/services/data-classification', label: 'Data Classification' },
        { path: '/services/policies-procedures', label: 'Policies & Procedures' },
        { path: '/services/dataflow-diagrams', label: 'Dataflow Diagrams' },
        { path: '/services/security-code-review', label: 'Security Code Review' }
      ]
    },
    {
      label: 'Vulnerability Management',
      children: [
        { path: '/vulnerability/scanning', label: 'Vulnerability Scanning' },
        { path: '/vulnerability/penetration-testing', label: 'Penetration Testing' },
        { path: '/vulnerability/red-teaming', label: 'Red Teaming' },
        { path: '/vulnerability/sast-dast', label: 'SAST / DAST' }
      ]
    },
    {
      label: 'Technologies',
      children: [
        {
          label: 'Detection and Response',
          children: [
            { path: '/technologies/edr', label: 'Endpoint Detection & Response' },
            { path: '/technologies/ndr', label: 'Network Detection & Response' }
          ]
        },
        { path: '/technologies/dlp', label: 'Data Leakage Prevention' },
        { path: '/technologies/security-awareness', label: 'Security Awareness & Phishing' },
        { path: '/technologies/siem', label: 'Security Information & Events Management' },
        { path: '/technologies/waf', label: 'Web Application Firewall' },
        { path: '/technologies/pam', label: 'Privileged Access Management' },
        { path: '/technologies/nac', label: 'Network Access Control' },
        { path: '/technologies/database-security', label: 'Database Security' }
      ]
    },
    {
      label: 'Managed Services',
      children: [
        { path: '/managed-services/soc', label: 'SOC Services' },
        { path: '/managed-services/noc', label: 'NOC Services' }
      ]
    },
    { path: '/contact', label: 'Contact' }
  ];

  const renderMenuItem = (item: any, index: number) => {
    const hasChildren = item.children && item.children.length > 0;
    const isActive = location.pathname === item.path;
    const dropdownKey = `dropdown-${index}`;
    const isDropdownOpen = openDropdowns[dropdownKey];
    const dropdownPosition = getDropdownPosition(index, menuItems.length);

    if (hasChildren) {
      return (
        <li key={index} className="nav-item">
          <button
            onClick={() => toggleDropdown(dropdownKey)}
            className={`nav-button ${isDropdownOpen ? 'dropdown-open' : ''}`}
          >
            <span>{item.label}</span>
            <FaChevronDown 
              className="dropdown-arrow"
              style={{ 
                transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
                fontSize: '12px'
              }} 
            />
          </button>
          <ul className={`dropdown-menu ${dropdownPosition} ${isDropdownOpen ? 'open' : ''}`}>
            {item.children.map((child: any, childIndex: number) => {
              if (child.children) {
                return (
                  <li key={childIndex} className="dropdown-item">
                    <button
                      onClick={() => {
                        // Close all other dropdowns first
                        setOpenDropdowns(prev => {
                          const newState: { [key: string]: boolean } = {};
                          newState[`${dropdownKey}-${childIndex}`] = !prev[`${dropdownKey}-${childIndex}`];
                          return newState;
                        });
                      }}
                      className={`nav-button ${openDropdowns[`${dropdownKey}-${childIndex}`] ? 'dropdown-open' : ''}`}
                      style={{ 
                        width: '100%', 
                        textAlign: 'left', 
                        padding: '12px 20px',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <span>{child.label}</span>
                      <FaChevronDown 
                        className="dropdown-arrow"
                        style={{ 
                          fontSize: '12px',
                          transform: openDropdowns[`${dropdownKey}-${childIndex}`] ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease'
                        }} 
                      />
                    </button>
                    <ul className={`dropdown-menu sub-dropdown-menu ${openDropdowns[`${dropdownKey}-${childIndex}`] ? 'open' : ''}`}>
                      {child.children.map((subChild: any, subIndex: number) => (
                        <li key={subIndex} className="dropdown-item">
                          <Link to={subChild.path} className="dropdown-link" onClick={closeAllDropdowns}>
                            {subChild.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              }
              return (
                <li key={childIndex} className="dropdown-item">
                  <Link to={child.path} className="dropdown-link" onClick={closeAllDropdowns}>
                    {child.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </li>
      );
    }

    return (
      <li key={index} className="nav-item">
        <Link to={item.path} className={`nav-link ${isActive ? 'active' : ''}`} onClick={closeAllDropdowns}>
          {item.label}
        </Link>
      </li>
    );
  };

  return (
    <nav className={`navbar-container ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-content align-items-left">
        <Link to="/" className="navbar-logo" onClick={closeAllDropdowns}>CYHY360</Link>
        
        <ul className={`nav-menu ${isOpen ? 'open' : ''}`}>
          {menuItems.map(renderMenuItem)}
        </ul>

        <button className="mobile-menu-button" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
